import Storage from 'disco-storage'
import debounce from 'lodash/debounce'
import get from 'lodash/get'
import type { ExplorerPane } from 'types/explorer'
import type { State, Thunk } from 'types/redux'
import type { Dispatch } from 'redux'
import type { MediaTrackEventPropertiesType } from 'types/media'
import type { TrackId } from 'types/track'
import type { EventSource, TrackEventOptions } from 'types/ui'
import type { ClientLibraryDiscoveryPanelItem } from 'types/client-library'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import { mapResponseToReducerObjects } from '@disco/state-management/actions/PlaylistTrackActions'
import api, { serializeAggregatedTrack } from '@disco/api/server'
import { normalizeV2Track } from '@disco/utils/search-page'
import {
  BACKGROUND_PLAYBACK_SERVICE_STATUS_SYNC,
  PLAYBACK_PANE_UPDATE,
  TRACK_ACTIVATE,
  TRACK_DURATION_UPDATE,
  TRACK_PAUSE,
  TRACK_PLAY,
  TRACK_MEDIA_PROPERTIES_UPDATE,
  TRACK_PLAY_PROGRESS_UPDATE,
  TRACK_PLAYLIST_PAGE_LOAD,
  TRACK_PLAYLIST_PAGE_LOAD_ERROR,
  TRACK_PLAYLIST_PAGE_LOAD_SUCCESS,
  TRACK_RESET,
  TRACK_SEEK,
  TRACK_STATUS_UPDATE,
  TRACK_STOP,
  TRACK_WAVEFORM_LOAD,
  TRACK_WAVEFORM_LOAD_ERROR,
  TRACK_WAVEFORM_LOAD_SUCCESS,
  VOLUME_CHANGE,
} from '@disco/constants/ActionTypes'
import { PHONE } from '@disco/constants/DeviceTypes'
import {
  getGroupForPlayback,
  getPlaybackPaneForMediaPlayback,
  getPlaylistForMediaPlayback,
  getPublicChannelSearchResults,
  getShortlistForMediaPlayback,
  getTrackPlaybackStatus,
  getTrackPlaying,
  isSearchPagePlayback,
} from '@disco/state-management/selectors/media'
import { getTracksByIds } from '@disco/state-management/selectors/tracks'
import { groupOperations } from '../ducks/shareable-channels/groups'
import { searchOperations } from '../ducks/shareable-channels/search'
/**
 * Options for track playback operations.
 * Contains context information about where the track is being played from.
 */
export type TrackOptions = {
  // TODO: merge with TrackEventOptions
  playlistId?: number | null | undefined
  shortlistId?: string | null | undefined
  shareId?: number | null | undefined
  groupId?: number | null | undefined
  paneIndex?: number | null | undefined
  campaignId?: number | null | undefined
  recipientId?: number | null | undefined
  clientLibraryId?: number | null | undefined
  offlineMode?: boolean
  paneReducer?: string
  tracklist?: number[] | null | undefined
  type?: string
  queue?: boolean
  playbackLocation?: string | null | undefined
  catalog?: ClientLibraryDiscoveryPanelItem | null | undefined
  playlistBusinessId?: number | null | undefined
}

export const setTrackMediaEventProperties = (
  eventProperties: MediaTrackEventPropertiesType,
) => {
  return (dispatch: Dispatch): void => {
    if (!eventProperties.trackId) {
      return
    }
    dispatch({
      type: TRACK_MEDIA_PROPERTIES_UPDATE,
      payload: {
        trackId: eventProperties.trackId,
        properties: eventProperties.properties,
      },
    })
  }
}

const logTrackPlay = (
  id: TrackId,
  source: EventSource,
  options: TrackEventOptions,
) => {
  try {
    api.sendTrackPlaybackEvent(id, source, options)
  } catch (_) {
    // continue regardless of error
  }
}

/*
  playbackPane resolution:
  - non-empty tracklist: no pane, next/previous walk the tracklist (paneIndex is ignored)
  - queue: keep the pane that is playing
  - otherwise: the pane at paneIndex, or none
*/
/**
 * Plays a track with the specified options and logs playback events.
 * @param originalTrackId - The ID of the track to play
 * @param mediaSource - The source context where play was initiated
 * @param options - Playback options including playlist, group, and pane information
 * @returns Thunk that initiates track playback and dispatches play action
 */
export function playTrack(
  originalTrackId: number,
  mediaSource: EventSource,
  options: TrackOptions,
): Thunk {
  const {
    playlistId,
    shortlistId,
    shareId,
    groupId,
    paneIndex,
    paneReducer,
    campaignId,
    recipientId,
    offlineMode,
    clientLibraryId,
    tracklist,
    queue,
    playbackLocation,
  } = options
  return (dispatch, getState) => {
    const state = getState()
    const currentTrack = state.media.trackOriginalId
    let playbackPane =
      paneIndex !== undefined && paneIndex !== null
        ? state[paneReducer || 'explorer'].panes[paneIndex]
        : undefined
    if (tracklist?.length) {
      // A leftover search pane would take precedence over the tracklist in nextHandler
      playbackPane = undefined
    } else if (queue) {
      // Lets users keep playing search results after navigating away from them in explorer
      playbackPane = getPlaybackPaneForMediaPlayback(state)
    }
    const currentTrackPlaybackStatus = currentTrack
      ? get(getState().media.trackStatus[currentTrack], 'playbackStatus', null)
      : null

    if (
      originalTrackId &&
      !offlineMode &&
      (currentTrack !== originalTrackId || !currentTrackPlaybackStatus)
    ) {
      logTrackPlay(originalTrackId, mediaSource, {
        playlist_id: playlistId,
        share_id: shareId,
        group_id: groupId,
        campaign_id: campaignId,
        recipient_id: recipientId,
        client_library_id: clientLibraryId,
      })
    }

    dispatch({
      type: TRACK_PLAY,
      trackId: originalTrackId,
      playlistId,
      shortlistId,
      shareId,
      groupId,
      playbackPane,
      campaignId,
      recipientId,
      mediaSource,
      offlineMode,
      clientLibraryId,
      tracklist: tracklist,
      playbackLocation,
    })
  }
}
const saveVolumeToStorage = debounce(
  (volume: number) => Storage.set('volume', volume),
  1000,
)
/**
 * Changes the playback volume and optionally saves it to storage.
 * @param volume - The new volume level (0-1)
 * @param saveToStorage - Whether to persist the volume setting
 * @returns Action object with new volume level
 */
export function changeVolume(
  volume: number,
  saveToStorage = true,
): {
  type: string
  volume: number
} {
  if (saveToStorage) {
    saveVolumeToStorage(volume)
  }

  return {
    type: VOLUME_CHANGE,
    volume,
  }
}
/**
 * Pauses playback of the specified track.
 * @param trackId - The ID of the track to pause
 * @returns Action object to pause the track
 */
export function pauseTrack(trackId: TrackId): {
  trackId: TrackId
  type: string
} {
  return {
    type: TRACK_PAUSE,
    trackId,
  }
}
/**
 * Stops playback of the specified track.
 * @param trackId - The ID of the track to stop
 * @returns Action object to stop the track
 */
export function stopTrack(trackId: TrackId): {
  trackId: TrackId
  type: string
} {
  return {
    type: TRACK_STOP,
    trackId,
  }
}
/**
 * Activates a track for playback without actually starting it.
 * @param trackId - The ID of the track to activate
 * @param playlistId - Optional playlist ID context
 * @param mediaSource - The source context for activation
 * @returns Action object to activate the track
 */
export function activateTrack(
  trackId: number,
  playlistId: number | null | undefined,
  mediaSource: string,
): {
  mediaSource: string
  playlistId: number | null | undefined
  trackId: number
  type: string
} {
  return {
    type: TRACK_ACTIVATE,
    trackId,
    playlistId,
    mediaSource,
  }
}
/**
 * Resets the entire playback state to initial values.
 * @returns Action object to reset playback state
 */
export function resetPlaybackState(): {
  type: string
} {
  return {
    type: TRACK_RESET,
  }
}
/**
 * Updates the duration of a track during playback.
 * @param trackId - The ID of the track to update
 * @param duration - The track duration in seconds
 * @returns Action object with updated track duration
 */
export function updateTrackDuration(
  trackId: TrackId,
  duration: number,
): {
  duration: number
  id: TrackId
  type: string
} {
  return {
    type: TRACK_DURATION_UPDATE,
    id: trackId,
    duration,
  }
}

// Nested/version tracks are never members of a top-level queue (playlist,
// tracklist, shortlist, etc). When one is played standalone (e.g. from a
// shared link), a plain findIndex() for it returns -1. Fall back to the
// position of its nesting_parent_id so "next"/"previous" behave the same
// as if the parent track were currently playing, instead of defaulting to
// the start of the queue.
function resolveQueuePosition(
  state,
  tracksQueue,
  trackId,
  getTrackId = t => t?.id,
) {
  const position = tracksQueue.findIndex(item => getTrackId(item) === trackId)
  if (position !== -1) return position

  const parentId = state.tracks[trackId]?.nesting_parent_id
  if (parentId == null) return -1

  return tracksQueue.findIndex(item => getTrackId(item) === parentId)
}

function findNextTrackId(
  currentPosition,
  tracksQueue,
  autoInvoked,
  deviceType,
  moveBackwards,
) {
  if (!tracksQueue) return null
  let position = currentPosition

  if (moveBackwards) {
    while (position-- > 0) {
      const previousTrack = tracksQueue[position]
      if (previousTrack) {
        return previousTrack.id
      }
    }
    return null
  }

  // position is -1 when neither the playing track nor its nesting parent
  // (resolveQueuePosition already tried both) is in this queue - nothing
  // reliable to advance to
  if (position < 0) return null

  const currentQueueTrack = tracksQueue[position]

  // special logic for phones:
  // if current track is audio, ensure that next track will be audio too
  // skip videos to not interrupt user work
  if (deviceType === PHONE && autoInvoked && !currentQueueTrack.video) {
    while (position++ < tracksQueue.length - 1) {
      const currentTrack = tracksQueue[position]
      const isPlayableAudio =
        currentTrack &&
        !currentTrack.video &&
        !currentTrack.virus &&
        currentTrack.uploaded
      if (isPlayableAudio) {
        return currentTrack.id
      }
    }

    return null
  }

  while (position++ < tracksQueue.length - 1) {
    const currentTrack = tracksQueue[position]
    const isPlayable =
      currentTrack && !currentTrack.virus && currentTrack.uploaded
    if (isPlayable) {
      return currentTrack.id
    }
  }

  return null
}

/**
 * Preloads the first track for faster playback startup.
 * @param trackId - The ID of the track to preload
 * @param mediaSource - The source context for preloading
 * @param options - Playback options for the preloaded track
 * @returns Action object to preload the track
 */
export function preloadFirstTrack(
  trackId: number,
  mediaSource: EventSource,
  options: TrackOptions,
): TrackOptions & {
  type: 'TRACK_PRELOAD'
  trackId: number
  mediaSource: EventSource
} {
  return { ...options, trackId, mediaSource, type: 'TRACK_PRELOAD' }
}
/**
 * Preloads the next track in the queue for seamless playback.
 * @returns Thunk that determines and preloads the next track
 */
export function preloadNextTrack(): Thunk {
  return (dispatch, getState) => {
    // silentDispatch wizardy to not rewrite `nextTrack`, and to try idea
    const silentDispatch = ({ type, ...action }) => {
      if (action.type === TRACK_PLAY) {
        if (getState().media.preloadingTrackId === action.trackId) {
          return
        }

        dispatch({
          type: 'TRACK_PRELOAD',
          ...action,
        })
      }
    }

    nextTrack(true, undefined, true)(silentDispatch, getState)
  }
}

const nextInTracklist = (
  state,
  dispatch: any,
  autoInvoked: boolean | null | undefined,
  customNextTrackId: TrackId | null | undefined,
  moveBackwards: boolean,
  noEvents?: boolean,
) => {
  const {
    tracklist,
    trackOriginalId,
    trackGroupId,
    trackMediaSource,
    trackPlaylistId,
    trackShareId,
    clientLibraryId,
    trackStatus,
  } = state.media
  const { mobileDeviceType } = state.app

  // Preserve playbackLocation so queue continues to show the original source
  const currentPlaybackLocation =
    trackStatus[trackOriginalId]?.playbackLocation ?? null

  const tracklistItems = getTracksByIds(state, {
    ids: state.media.tracklist,
  })
  const position = resolveQueuePosition(state, tracklistItems, trackOriginalId)
  const nextTrackId =
    customNextTrackId ||
    findNextTrackId(
      position,
      tracklistItems,
      autoInvoked,
      mobileDeviceType,
      moveBackwards,
    )

  if (nextTrackId) {
    // trackMediaSource is null only before the first play; skip logging rather than sending a sourceless event
    if (!noEvents && trackMediaSource) {
      // TODO: pass playbackContext to all logTrackPlay in this file
      logTrackPlay(nextTrackId, trackMediaSource, {
        group_id: trackGroupId,
        client_library_id: clientLibraryId,
        playlist_id: trackPlaylistId,
        share_id: trackShareId,
      })
    }

    dispatch({
      type: TRACK_PLAY,
      trackId: nextTrackId,
      playlistId: trackPlaylistId,
      shareId: trackShareId,
      mediaSource: trackMediaSource,
      clientLibraryId,
      tracklist,
      playbackLocation: currentPlaybackLocation,
    })
  } else {
    dispatch({
      type: TRACK_STOP,
      trackId: trackOriginalId,
    })
  }
}

const nextInTrackPane = (
  state,
  dispatch: any,
  autoInvoked: boolean | null | undefined,
  customNextTrackId: TrackId | null | undefined,
  moveBackwards: boolean,
  noEvents?: boolean,
) => {
  const { trackOriginalId, trackGroupId, trackMediaSource, trackStatus } =
    state.media
  const { mobileDeviceType } = state.app
  const playbackPane = getPlaybackPaneForMediaPlayback(state)
  if (!playbackPane)
    return dispatch({
      type: TRACK_STOP,
      trackId: trackOriginalId,
    })
  // Preserve playbackLocation so Mixpanel Context stays correct on auto-advance
  const currentPlaybackLocation =
    trackStatus[trackOriginalId]?.playbackLocation ?? null
  const results = playbackPane.results
  const position = resolveQueuePosition(state, results ?? [], trackOriginalId)
  const nextTrackId =
    customNextTrackId ||
    findNextTrackId(
      position,
      results,
      autoInvoked,
      mobileDeviceType,
      moveBackwards,
    )

  if (nextTrackId) {
    const nextTrack = state.tracks[nextTrackId]

    // trackMediaSource is null only before the first play; skip logging rather than sending a sourceless event
    if (!noEvents && trackMediaSource) {
      logTrackPlay(nextTrackId, trackMediaSource, {
        group_id: trackGroupId,
        client_library_id: nextTrack?.origin?.source_library?.id, // for aggregated search
      })
    }

    dispatch({
      type: TRACK_PLAY,
      trackId: nextTrackId,
      groupId: trackGroupId,
      mediaSource: trackMediaSource,
      playbackPane,
      playbackLocation: currentPlaybackLocation,
    })
  } else {
    dispatch({
      type: TRACK_STOP,
      trackId: trackOriginalId,
    })
  }

  if (
    results &&
    !moveBackwards &&
    position > results.length - 20 &&
    playbackPane.next
  ) {
    dispatch(loadPlaybackPaneNextPage(playbackPane))
  }
}

const nextInPlaylist = (
  state,
  dispatch,
  autoInvoked: boolean | null | undefined,
  customNextTrackId: TrackId | null | undefined,
  moveBackwards: boolean,
  noEvents?: boolean,
) => {
  const {
    trackPlaylistId,
    trackOriginalId,
    trackShortlistId,
    trackShareId,
    trackGroupId,
    trackMediaSource,
    trackCampaignId,
    trackRecipientId,
    trackStatus,
  } = state.media
  const { mobileDeviceType } = state.app
  // Preserve playbackLocation so Mixpanel Context stays correct on auto-advance
  const currentPlaybackLocation =
    trackStatus[trackOriginalId]?.playbackLocation ?? null
  const playlist = trackPlaylistId
    ? getPlaylistForMediaPlayback(state, {
        id: trackPlaylistId,
      })
    : null
  if (!playlist)
    return dispatch({
      type: TRACK_STOP,
      trackId: trackOriginalId,
    })
  const position = resolveQueuePosition(state, playlist.tracks, trackOriginalId)
  const nextTrackId =
    customNextTrackId ||
    findNextTrackId(
      position,
      playlist.tracks,
      autoInvoked,
      mobileDeviceType,
      moveBackwards,
    )

  if (nextTrackId) {
    // trackMediaSource is null only before the first play; skip logging rather than sending a sourceless event
    if (!noEvents && trackMediaSource) {
      logTrackPlay(nextTrackId, trackMediaSource, {
        playlist_id: trackPlaylistId,
        share_id: trackShareId,
        group_id: trackGroupId,
        campaign_id: trackCampaignId,
        recipient_id: trackRecipientId,
      })
    }

    dispatch({
      type: TRACK_PLAY,
      trackId: nextTrackId,
      playlistId: trackPlaylistId,
      shortlistId: trackShortlistId,
      shareId: trackShareId,
      groupId: trackGroupId,
      campaignId: trackCampaignId,
      recipientId: trackRecipientId,
      mediaSource: trackMediaSource,
      playbackLocation: currentPlaybackLocation,
    })
  } else {
    dispatch({
      type: TRACK_STOP,
      trackId: trackOriginalId,
    })
  }

  // TODO: dirty implementation of next playlist page loading,
  // should be called in playTrack too and need to check if we are not
  // already loading this page
  if (
    playlist &&
    !moveBackwards &&
    position > playlist.tracks.length - 20 &&
    playlist.tracks_info &&
    playlist.tracks_info.next
  ) {
    dispatch(loadNextQueuePage(playlist.tracks_info.next))
  }
}

const nextInShortlist = (
  state,
  dispatch,
  autoInvoked: boolean | null | undefined,
  customNextTrackId: TrackId | null | undefined,
  moveBackwards: boolean,
  noEvents?: boolean,
) => {
  const {
    trackPlaylistId,
    trackOriginalId,
    trackShortlistId,
    trackShareId,
    trackMediaSource,
    trackStatus,
  } = state.media
  const { mobileDeviceType } = state.app
  // Preserve playbackLocation so Mixpanel Context stays correct on auto-advance
  const currentPlaybackLocation =
    trackStatus[trackOriginalId]?.playbackLocation ?? null
  const shortlist = getShortlistForMediaPlayback(state, {
    id: trackShortlistId,
  })

  if (!shortlist || shortlist.items.length === 0) {
    return dispatch({
      type: TRACK_RESET,
    })
  }

  const position = resolveQueuePosition(
    state,
    shortlist.items,
    trackOriginalId,
    i => i.track.id,
  )
  const tracksQueue = shortlist.items.map(item => item.track)
  const nextTrackId =
    customNextTrackId ||
    findNextTrackId(
      position,
      tracksQueue,
      autoInvoked,
      mobileDeviceType,
      moveBackwards,
    )

  if (nextTrackId) {
    // trackMediaSource is null only before the first play; skip logging rather than sending a sourceless event
    if (!noEvents && trackMediaSource) {
      logTrackPlay(nextTrackId, trackMediaSource, {
        playlist_id: trackPlaylistId,
        share_id: trackShareId,
      })
    }

    dispatch({
      type: TRACK_PLAY,
      trackId: nextTrackId,
      playlistId: trackPlaylistId,
      shortlistId: trackShortlistId,
      mediaSource: trackMediaSource,
      playbackLocation: currentPlaybackLocation,
    })
  } else {
    dispatch({
      type: TRACK_STOP,
      trackId: trackOriginalId,
    })
  }
}

const nextInPublicGroup = (
  state,
  dispatch: any,
  autoInvoked: boolean | null | undefined,
  customNextTrackId: TrackId | null | undefined,
  moveBackwards: boolean,
  noEvents?: boolean,
) => {
  const { trackOriginalId, trackGroupId, trackMediaSource, trackStatus } =
    state.media
  const { mobileDeviceType } = state.app
  // Preserve playbackLocation so Mixpanel Context stays correct on auto-advance
  const currentPlaybackLocation =
    trackStatus[trackOriginalId]?.playbackLocation ?? null
  const group = getGroupForPlayback(state, {
    id: trackGroupId,
  })
  if (!group) return
  const position = resolveQueuePosition(state, group.items, trackOriginalId)
  const nextTrackId =
    customNextTrackId ||
    findNextTrackId(
      position,
      group.items,
      autoInvoked,
      mobileDeviceType,
      moveBackwards,
    )

  if (nextTrackId) {
    // trackMediaSource is null only before the first play; skip logging rather than sending a sourceless event
    if (!noEvents && trackMediaSource) {
      logTrackPlay(nextTrackId, trackMediaSource, {
        group_id: trackGroupId,
      })
    }

    dispatch({
      type: TRACK_PLAY,
      trackId: nextTrackId,
      groupId: trackGroupId,
      mediaSource: trackMediaSource,
      playbackLocation: currentPlaybackLocation,
    })
  } else {
    dispatch({
      type: TRACK_STOP,
      trackId: trackOriginalId,
    })
  }

  if (
    !moveBackwards &&
    position > group.items.length - 20 &&
    group.nextPage &&
    trackGroupId
  ) {
    dispatch(groupOperations.loadGroupContentsNext(trackGroupId))
  }
}

const nextInPublicSearchResults = (
  state,
  dispatch: any,
  autoInvoked: boolean | null | undefined,
  customNextTrackId: TrackId | null | undefined,
  moveBackwards: boolean,
  noEvents?: boolean,
) => {
  const { trackOriginalId, trackGroupId, trackMediaSource, trackStatus } =
    state.media
  const { mobileDeviceType } = state.app
  // Preserve playbackLocation so Mixpanel Context stays correct on auto-advance
  const currentPlaybackLocation =
    trackStatus[trackOriginalId]?.playbackLocation ?? null
  // @ts-expect-error - TODO: fix this
  const searchResults = getPublicChannelSearchResults(state, {
    id: trackGroupId,
  })
  if (!searchResults) return
  const position = resolveQueuePosition(
    state,
    searchResults.results,
    trackOriginalId,
  )
  const nextTrackId =
    customNextTrackId ||
    findNextTrackId(
      position,
      searchResults.results,
      autoInvoked,
      mobileDeviceType,
      moveBackwards,
    )

  if (nextTrackId) {
    // trackMediaSource is null only before the first play; skip logging rather than sending a sourceless event
    if (!noEvents && trackMediaSource) {
      logTrackPlay(nextTrackId, trackMediaSource, {
        group_id: trackGroupId,
      })
    }

    dispatch({
      type: TRACK_PLAY,
      trackId: nextTrackId,
      groupId: trackGroupId,
      mediaSource: trackMediaSource,
      playbackLocation: currentPlaybackLocation,
    })
  } else {
    dispatch({
      type: TRACK_STOP,
      trackId: trackOriginalId,
    })
  }

  if (
    !moveBackwards &&
    position > searchResults.results.length - 20 &&
    searchResults.next
  ) {
    dispatch(searchOperations.publicSearchNext())
  }
}

const nextHandler = (
  state,
  dispatch,
  autoInvoked,
  customNextTrackId,
  moveBackwards,
  noEvents?: boolean,
) => {
  const {
    tracklist,
    trackPlaylistId,
    trackOriginalId,
    trackShortlistId,
    trackGroupId,
  } = state.media
  let func:
    | ReturnType<typeof nextInTracklist>
    | ReturnType<typeof nextInTrackPane>
    | ReturnType<typeof nextInPublicSearchResults>
    | ReturnType<typeof nextInPublicGroup>
    | ReturnType<typeof nextInPlaylist>
    | ReturnType<typeof nextInShortlist>
    | null = null

  // Search page playback always uses snapshotted tracklist. This check must
  // come first because state.explorer.playbackPane may still be set from
  // previous navigation, even though we're now playing from the search page.
  // If tracklist is empty, nextInTracklist will stop playback (end of list).
  if (isSearchPagePlayback(state)) {
    func = nextInTracklist
  } else if (getPlaybackPaneForMediaPlayback(state)) {
    func = nextInTrackPane
  } else if (tracklist?.length) {
    // Generic tracklist playback (non-search page sources)
    func = nextInTracklist
  } else if (
    // @ts-expect-error - TODO: fix this
    getPublicChannelSearchResults(state, {
      id: trackGroupId,
    })
  ) {
    func = nextInPublicSearchResults
  } else if (
    getGroupForPlayback(state, {
      id: trackGroupId,
    })
  ) {
    func = nextInPublicGroup
  } else if (trackPlaylistId && !trackShortlistId) {
    func = nextInPlaylist
  } else if (trackShortlistId) {
    func = nextInShortlist
  }

  if (func) {
    return func(
      state,
      dispatch,
      autoInvoked,
      customNextTrackId,
      moveBackwards,
      noEvents,
    )
  }

  return dispatch({
    type: TRACK_STOP,
    trackId: trackOriginalId,
  })
}

/**
 * Advances to the next track in the current playback context.
 * @param autoInvoked - Whether this was called automatically (affects mobile behavior)
 * @param nextTrackId - Optional specific track ID to play next
 * @param noEvents - Whether to skip logging playback events
 * @returns Thunk that plays the next track or stops if at end
 */
export const nextTrack =
  (
    autoInvoked?: boolean,
    nextTrackId?: TrackId | null | undefined,
    noEvents: boolean = false,
  ): Thunk =>
  (dispatch, getState) =>
    nextHandler(getState(), dispatch, autoInvoked, nextTrackId, false, noEvents)
/**
 * Goes back to the previous track or restarts current track if past 3 seconds.
 * @param customPrevTrackId - Optional specific track ID to play previous
 * @returns Thunk that plays previous track or seeks to beginning
 */
export const previousTrack =
  (customPrevTrackId?: TrackId | null | undefined): Thunk =>
  (dispatch, getState) => {
    const state = getState()

    if (!customPrevTrackId) {
      const track = getTrackPlaying(state)
      const status = track
        ? getTrackPlaybackStatus(state, {
            trackId: track.id,
          })
        : null

      if (track && status && status?.playbackPosition >= 3) {
        return dispatch(seekTrackPosition(track.id, 0))
      }
    }

    return nextHandler(state, dispatch, false, customPrevTrackId, true)
  }
/**
 * Seeks to a specific position in the currently playing track.
 * @param trackId - The ID of the track to seek
 * @param position - The position to seek to in seconds
 * @returns Action object to seek track position
 */
export function seekTrackPosition(
  trackId: TrackId,
  position: number,
  location?: string | null | undefined,
): {
  position: number
  trackId: TrackId
  location?: string | null | undefined
  type: string
} {
  return {
    type: TRACK_SEEK,
    trackId,
    position,
    location,
  }
}

/**
 * Updates the playback and load status of a track.
 * @param trackId - The ID of the track to update
 * @param loadStatus - The loading status (e.g., 'loading', 'loaded')
 * @param playbackStatus - The playback status (e.g., 'playing', 'paused')
 * @returns Action object with updated track status
 */
export function updatePlaybackStatus(
  trackId: TrackId,
  loadStatus: string,
  playbackStatus: string,
): {
  loadStatus: string
  playbackStatus: string
  trackId: TrackId
  type: string
} {
  return {
    type: TRACK_STATUS_UPDATE,
    trackId,
    loadStatus,
    playbackStatus,
  }
}
/**
 * Updates the playback progress position of a track.
 * @param trackId - The ID of the track being played
 * @param progress - The current playback position in seconds
 * @returns Action object with updated playback progress
 */
export function updatePlaybackProgress(
  trackId: TrackId,
  progress: number,
): {
  progress: number
  trackId: TrackId
  type: string
} {
  return {
    type: TRACK_PLAY_PROGRESS_UPDATE,
    trackId,
    progress,
  }
}
/**
 * Loads the next page of tracks in a playlist queue.
 * @param url - The URL for the next page of playlist tracks
 * @returns Thunk that fetches and adds tracks to the current playlist
 */
export function loadNextQueuePage(url: string): Thunk {
  return (dispatch, getState) => {
    dispatch({
      type: TRACK_PLAYLIST_PAGE_LOAD,
    })
    return api
      .getPlaylistPage(url)
      .then(result => {
        mapResponseToReducerObjects(dispatch, getState, result)
        dispatch({
          type: TRACK_PLAYLIST_PAGE_LOAD_SUCCESS,
        })
      })
      .catch(error => {
        dispatch({
          type: TRACK_PLAYLIST_PAGE_LOAD_ERROR,
          error,
        })
      })
  }
}
/**
 * Loads the next page of tracks in the current playback pane.
 * @param pane - The explorer pane to load more tracks for
 * @returns Thunk that fetches and adds tracks to the pane
 */
export function loadPlaybackPaneNextPage(pane: ExplorerPane): Thunk {
  return (dispatch, getState) => {
    if (!pane.next) return
    const groupHeader = pane.groupId ? JSON.stringify([pane.groupId]) : ''
    let res

    if (pane.isPlayingAggregatedSearchTrack) {
      // Aggregated results paginate the v2 search endpoint with a cursor.
      const queryV2 = pane.aggregatedSearchResults?.queryV2
      if (!queryV2) return
      res = api.searchV2({ ...queryV2, cursor: pane.next }).then(response => ({
        results: response.hits
          .map(normalizeV2Track)
          .map(serializeAggregatedTrack),
        next: response.pagination.cursor,
      }))
    } else {
      res = api.getPane(groupHeader, pane.next)
    }

    return res
      .then(result => {
        const obj = mapResponseToReducerObjects(dispatch, getState, result)
        const newPane = { ...pane }

        if (pane.isPlayingAggregatedSearchTrack) {
          newPane.aggregatedSearchResults = {
            searchResults: [
              ...(pane.aggregatedSearchResults?.searchResults ?? []),
              ...obj.results,
            ],
            next: result.next,
            queryV2: pane.aggregatedSearchResults?.queryV2,
          }
        } else {
          newPane.results = [...(pane.results ?? []), ...obj.results]
          newPane.next = result.next
        }

        dispatch(updatePlaybackPane(newPane))
      })
      .catch(error => {
        dispatch(sendGenericError(error))
      })
  }
}
/**
 * Loads the waveform data for a track.
 * @param trackId - The ID of the track to load waveform for
 * @returns Thunk that fetches and dispatches track waveform data
 */
export function getTrackWaveform(trackId: TrackId): Thunk {
  return dispatch => {
    dispatch({
      type: TRACK_WAVEFORM_LOAD,
      trackId,
    })
    api
      .getTrackWaveform(trackId)
      .then(waveform => {
        dispatch({
          type: TRACK_WAVEFORM_LOAD_SUCCESS,
          trackId,
          waveform,
        })
      })
      .catch(error => {
        dispatch({
          type: TRACK_WAVEFORM_LOAD_ERROR,
          trackId,
          error,
        })
      })
  }
}

/**
 * Synchronizes playback state with background playback service.
 * @param params - Object containing track ID and playback status
 * @param params.trackId - Optional track ID being played
 * @param params.playbackStatus - Current playback status from background service
 * @returns Action object to sync with background playback service
 */
export const syncWithBackgroundPlaybackService = ({
  trackId,
  playbackStatus,
}: {
  trackId?: TrackId
  playbackStatus: string
}): {
  playbackStatus: string
  trackId: void | TrackId
  type: string
} => ({
  type: BACKGROUND_PLAYBACK_SERVICE_STATUS_SYNC,
  trackId,
  playbackStatus,
})
/**
 * Updates the current playback pane with new data.
 * @param pane - The explorer pane data to update
 * @returns Action object to update the playback pane
 */
export const updatePlaybackPane = (
  pane: ExplorerPane,
): {
  pane: ExplorerPane
  type: string
} => ({
  type: PLAYBACK_PANE_UPDATE,
  pane,
})
